import classNames from 'classnames';
import styles from './LinkButton.module.scss';
import { NextLink } from '../Link/NextLink/NextLink';
import { MouseEventHandler } from 'react';
import { Icon } from 'ui/components';

export interface LinkButtonProps {
	children: React.ReactNode;
	state?: 'inactive';
	style?: 'primary' | 'secondary' | 'inactive' | 'sm' | 'primaryWeb' | 'secondaryWeb' | 'tertiaryWeb';
	title?: string;
	className?: string;
	url?: string;
	target?: string;
	withBackIcon?: React.ReactNode;
	withFrontIcon?: React.ReactNode;
	iconClassName?: string;
	ariaLabel?: string;
	handleClick?: MouseEventHandler<HTMLAnchorElement> | (() => void);
	disabled?: boolean;
	showSpinner?: boolean;
}

export const LinkButton: React.FC<LinkButtonProps> = ({
	children,
	style,
	title,
	className,
	state,
	url,
	target,
	withBackIcon,
	withFrontIcon,
	iconClassName,
	ariaLabel,
	handleClick,
	disabled,
	showSpinner,
}) => {
	const handleClickInternal: MouseEventHandler<HTMLAnchorElement> = (e) => {
		if (disabled) {
			e.preventDefault();
			return;
		}
		handleClick?.(e);
	};

	return (
		<NextLink
			href={url ?? '/'}
			target={target}
			title={title}
			aria-label={ariaLabel}
			onClick={handleClickInternal}
			aria-disabled={disabled}
			className={classNames(
				styles.LinkButton,
				styles[`LinkButton___${style}`],
				styles[`LinkButton___${state}`],
				{
					[styles.LinkButton___hasIcon]: withBackIcon || withFrontIcon,
					[styles.LinkButton___disabled]: disabled,
				},
				className,
			)}
		>
			{showSpinner && <span className={styles.LinkButton_spinner} />}
			{withFrontIcon && !showSpinner && (
				<Icon
					size={style === 'primaryWeb' || style === 'secondaryWeb' ? 'md' : 'sm'}
					className={classNames(styles.LinkButton_buttonIcon, styles.LinkButton_buttonIcon___front, iconClassName)}
				>
					{withFrontIcon}
				</Icon>
			)}

			{children}

			{withBackIcon && !showSpinner && (
				<Icon size="sm" className={classNames(styles.LinkButton_buttonIcon, styles.LinkButton_buttonIcon___back, iconClassName)}>
					{withBackIcon}
				</Icon>
			)}
		</NextLink>
	);
};
